// The console, assembled: the API client, the server-state cache, the router, localisation and the
// theme, for one surface — the venue and organisation console or the HQ console (`src/surface.tsx`).
// `main.tsx` mounts it in the browser with the surface its build was made for; the tests mount either
// with a fake API and a memory history.

import type { ApiClient } from '@clavian/api-client';
import { ClavianI18nProvider } from '@clavian/i18n/react';
import { ClavianThemeProvider, ToastProvider, TooltipProvider } from '@clavian/ui-web';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, type RouterHistory } from '@tanstack/react-router';
import { Fragment, useEffect, useState } from 'react';

import { ApiProvider } from './api/client';
import { onWriteSucceeded } from './api/writes';
import { needsSecondFactor } from './api/problems';
import { forgetInvitationOnSignOut } from './auth/invitation-token';
import { createSecondFactorSignal, SecondFactorProvider, type SecondFactorSignal } from './auth/second-factor-gate';
import { createConsoleI18n, DEFAULT_VENUE_LOCALE, type ConsoleI18n } from './i18n';
import { createConsoleRouter } from './router';
import { invalidateSetup } from './setup/queries';
import { useConsoleTheme } from './shell/theme';
import { SurfaceProvider, type ConsoleSurface } from './surface';

export function createConsoleQueryClient(signal: SecondFactorSignal): QueryClient {
  const onError = (error: unknown) => {
    // Whichever read or write meets the console gate first brings up the step-up screen (spec/07 §6.6).
    if (needsSecondFactor(error)) signal.raise();
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // A refusal is an answer: retrying a 4xx only delays the screen that explains it.
        retry: (failures, error) => failures < 2 && !(error instanceof Error && 'status' in error && Number(error.status) < 500),
      },
    },
  });
}

export interface ConsoleAppProps {
  readonly api: ApiClient;
  /** Which console: the build's own in the browser (`clavian:surface`), either in a test. */
  readonly surface: ConsoleSurface;
  /** A memory history in tests; the browser's own otherwise. */
  readonly history?: RouterHistory;
  readonly i18n?: ConsoleI18n;
}

export function ConsoleApp({ api: givenApi, surface, history, i18n: givenI18n }: ConsoleAppProps) {
  // Every sign-out, wherever it is made, forgets an invitation kept in this browser (`invitation-token.ts`).
  const [api] = useState(() => forgetInvitationOnSignOut(givenApi));
  const [signal] = useState<SecondFactorSignal>(createSecondFactorSignal);
  const [queryClient] = useState(() => createConsoleQueryClient(signal));
  const [router] = useState(() => createConsoleRouter({ api, queryClient }, surface.createRouteTree(), history));
  const [i18n] = useState(() => givenI18n ?? createConsoleI18n(undefined, surface.resources));
  // Dark, light or the device's own (ADR-0041): the member's choice on this device, dark until they make one.
  const { theme } = useConsoleTheme();

  // Any write the console makes may tick a setup step: the Setup page and the sidebar's "n left" re-read at once, every other
  // setup view on its next mount (`setup/queries.ts`).
  useEffect(() => onWriteSucceeded(() => void invalidateSetup(queryClient)), [queryClient]);

  useEffect(() => {
    // The page says which language it is in, for screen readers and hyphenation.
    const sync = (language: string) => document.documentElement.setAttribute('lang', language);
    sync(i18n.language);
    i18n.on('languageChanged', sync);
    return () => i18n.off('languageChanged', sync);
  }, [i18n]);

  useEffect(() => {
    document.documentElement.dataset['surface'] = surface.name;
  }, [surface.name]);

  // The demo's gate and banner (spec/24 §4.3); the other surfaces have no frame.
  const Frame = surface.Frame ?? Fragment;

  return (
    <ApiProvider api={api}>
      <SurfaceProvider surface={surface}>
        <QueryClientProvider client={queryClient}>
          <ClavianI18nProvider venue={DEFAULT_VENUE_LOCALE} i18n={i18n}>
            {/* Eclipse, dark by default with a light theme to choose (ADR-0060, ADR-0041); a venue's brand overrides it at runtime. */}
            <ClavianThemeProvider mode={theme} accent="brand" className="console-root">
              <TooltipProvider>
                <ToastProvider>
                  <SecondFactorProvider signal={signal}>
                    <Frame>
                      <RouterProvider router={router} />
                    </Frame>
                  </SecondFactorProvider>
                </ToastProvider>
              </TooltipProvider>
            </ClavianThemeProvider>
          </ClavianI18nProvider>
        </QueryClientProvider>
      </SurfaceProvider>
    </ApiProvider>
  );
}
